import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  AppBar,
  Toolbar,
  CircularProgress,
  Chip,
  Grid,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  PlayArrow as PlayIcon,
  Person as SoloIcon,
  Groups as LiveIcon,
  LocalFireDepartment as HotSeatIcon,
  Refresh as RefreshIcon,
  Casino as WheelIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import io from "socket.io-client";
import confetti from "canvas-confetti";
import UserAuthButton from "../auth/UserAuthButton";
import { useAuthContext } from "../../../context/AuthContext";
import LiveWheelDisplay from "../../Common/LiveWheelDisplay";
import DefaultStyleWheel, { prizeIndexFromSpin } from "../../Common/DefaultStyleWheel";
import SoundMuteButton from "../../Common/SoundMuteButton";
import useGameSounds from "../../../hooks/useGameSounds";
import HotSeatAnnounceModal from "../../games/HotSeatAnnounceModal";
import useGameHistory from "../../hooks/useGameHistory";
import {
  SPIN,
  spinAppBarSx,
  spinFadeUp,
  spinHotBtnSx,
  spinModeTileSx,
  spinPageSx,
  spinPanelSx,
  spinPrimaryBtnSx,
  spinPulse,
  spinTitleGradientSx,
} from "./spinTheme";
import {
  SW_GAME_TYPES,
  SW_PHASES,
  isWheelVisiblePhase,
  isUserWheelPhase,
} from "./spinHostPhases";
import { renderSpinHostPhaseView } from "./SpinHostPhaseViews";
import { bindGameResync } from "../../../utils/bindGameResync";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const firePrizeConfetti = () => {
  const end = Date.now() + 1800;
  const colors = ["#FFD700", "#ff6b3d", "#ff2d8a", "#4ecdc4", "#ffffff"];
  const frame = () => {
    confetti({
      particleCount: 5,
      angle: 60 + Math.random() * 60,
      spread: 70,
      origin: { x: Math.random(), y: Math.random() * 0.45 },
      colors,
      scalar: 1.05,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  };
  frame();
};

const SpinningWheel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, isLoading } = useAuthContext();
  const searchParams = new URLSearchParams(location.search);
  const forceJoin = searchParams.get("forceJoin") === "1";
  const isGigMode = ["1", "true", "yes"].includes(
    (searchParams.get("gigmode") || "").toLowerCase(),
  );

  const [playMode, setPlayMode] = useState("pick");
  const { goTo: goToGameScreen, goBack: goBackGameScreen } = useGameHistory({
    game: "spinning_wheel",
    onScreen: (screen, data) => {
      if (forceJoin) return;
      if (screen === "home" || screen === "mode") {
        setPlayMode("pick");
        return;
      }
      if (screen === "game" && data?.playMode) {
        setPlayMode(data.playMode);
      }
    },
  });

  useEffect(() => {
    if (isLoading) return;
    const mode = new URLSearchParams(location.search).get("mode");
    if (mode === "host" || mode === "sp" || forceJoin) setPlayMode("host");
  }, [isLoading, location.search, forceJoin]);

  // Default (solo) wheel
  const [wheel, setWheel] = useState(null);
  const [soloLoading, setSoloLoading] = useState(false);
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [livePrizeIndex, setLivePrizeIndex] = useState(0);

  // Live / host wheel
  const [isLiveWheel, setIsLiveWheel] = useState(false);
  const [liveMode, setLiveMode] = useState("participants");
  const [liveEntries, setLiveEntries] = useState([]);
  const liveEntriesRef = useRef([]);
  liveEntriesRef.current = liveEntries;
  const [liveSpinning, setLiveSpinning] = useState(false);
  const [liveFinalAngle, setLiveFinalAngle] = useState(0);
  const [liveSpinDuration, setLiveSpinDuration] = useState(6);
  const [liveWinner, setLiveWinner] = useState(null);
  const [liveAnimComplete, setLiveAnimComplete] = useState(false);
  const [liveTitle, setLiveTitle] = useState("Users Wheel");
  const [hotSeatInfo, setHotSeatInfo] = useState(null);
  const [prizeWheelMeta, setPrizeWheelMeta] = useState(null);
  const [hotSeatSpinBusy, setHotSeatSpinBusy] = useState(false);
  const [hostLoading, setHostLoading] = useState(false);
  const [hostPreview, setHostPreview] = useState(null);
  const [hotSeatAnnounce, setHotSeatAnnounce] = useState(null);
  const [hostPhase, setHostPhase] = useState(null);
  const [hostGameType, setHostGameType] = useState(null);
  const [announceWheelData, setAnnounceWheelData] = useState(null);
  const [userWheelEntries, setUserWheelEntries] = useState([]);
  const [uwSpinning, setUwSpinning] = useState(false);
  const [uwFinalAngle, setUwFinalAngle] = useState(0);
  const [uwSpinDuration, setUwSpinDuration] = useState(6);
  const [uwWinner, setUwWinner] = useState(null);
  const [uwAnimComplete, setUwAnimComplete] = useState(false);

  const playModeRef = useRef("pick");
  const isLiveRef = useRef(false);
  const liveModeRef = useRef("participants");
  const hostPhaseRef = useRef(null);
  const socketRef = useRef(null);
  const announcedHotKeyRef = useRef(null);
  const wheelBoxRef = useRef(null);
  const liveFetchGenRef = useRef(0);
  const fetchLiveWheelRef = useRef(null);
  const applyLivePayloadRef = useRef(null);
  const clearLiveStateRef = useRef(null);
  const [wheelBoxSize, setWheelBoxSize] = useState(360);
  const { play, playLoop, tryStartBgMusic, stopBgMusic, loaded: soundsLoaded, sounds } =
    useGameSounds("spin_wheel");
  const playRef = useRef(play);
  const playLoopRef = useRef(playLoop);
  const tryStartBgMusicRef = useRef(tryStartBgMusic);

  useEffect(() => {
    playRef.current = play;
    playLoopRef.current = playLoop;
    tryStartBgMusicRef.current = tryStartBgMusic;
  }, [play, playLoop, tryStartBgMusic]);

  useEffect(() => {
    hostPhaseRef.current = hostPhase;
  }, [hostPhase]);

  useEffect(() => {
    playModeRef.current = playMode;
    setError(null);
  }, [playMode]);

  useEffect(() => {
    isLiveRef.current = isLiveWheel;
  }, [isLiveWheel]);

  useEffect(() => {
    liveModeRef.current = liveMode;
  }, [liveMode]);

  useEffect(() => {
    const el = wheelBoxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      const resultReserve = 64;
      const next = Math.max(220, Math.min(440, Math.min(width, (height || width) - resultReserve) - 4));
      setWheelBoxSize(next);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [playMode, isLiveWheel, wheel]);

  const getDeviceId = () => {
    let deviceId = localStorage.getItem("device_id");
    if (!deviceId) {
      deviceId = "device_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
      localStorage.setItem("device_id", deviceId);
    }
    return deviceId;
  };

  const getAuthToken = () => localStorage.getItem("auth_token");

  const applyLivePayload = useCallback((liveData, { reconcileMotion = false } = {}) => {
    const entries = liveData.entries || [];
    const mode = liveData.liveMode || "participants";
    const phase =
      liveData.phase ||
      liveData.host?.phase ||
      (entries.length >= 2 ? SW_PHASES.WHEEL_READY : SW_PHASES.INTRO);
    const gameType =
      liveData.gameType ||
      liveData.host?.gameType ||
      (mode === "hot_seat" ? SW_GAME_TYPES.HOT_SEAT : SW_GAME_TYPES.USER_WHEEL);

    setHostPhase(phase);
    setHostGameType(gameType);
    setLiveMode(mode);
    setIsLiveWheel(true);
    setLiveEntries(entries);

    // Announce wheel info (for ANNOUNCE phase slide)
    if (liveData.announceWheel) {
      setAnnounceWheelData(liveData.announceWheel);
    }

    // User wheel entries (for USER_WHEEL_READY phase)
    if (liveData.userWheelEntries) {
      setUserWheelEntries(liveData.userWheelEntries);
    }

    if (mode === "hot_seat") {
      setHotSeatInfo(liveData.hotSeat || liveData.host?.selectedHotSeat || null);
      setPrizeWheelMeta(liveData.prizeWheel || null);
      setLiveTitle(liveData.prizeWheel?.wheel_name || liveData.session?.title || "Hot Seat");
    } else {
      setHotSeatInfo(null);
      setPrizeWheelMeta(null);
      setLiveTitle(liveData.session?.title || "Users Wheel");
    }
    setHostPreview({
      liveMode: mode,
      phase,
      gameType,
      title:
        liveData.prizeWheel?.wheel_name ||
        liveData.session?.title ||
        (mode === "hot_seat" ? "Hot Seat" : "Users Wheel"),
      hotName:
        liveData.hotSeat?.display_name ||
        liveData.host?.selectedHotSeat?.display_name ||
        null,
    });
    if (liveData.session?.winner_entry_id) {
      const w = entries.find((e) => e.id === liveData.session.winner_entry_id);
      if (w && !liveSpinning) setLiveWinner(w);
    }
    if (reconcileMotion) {
      if (phase !== SW_PHASES.SPINNING) setLiveSpinning(false);
      const hotSeatSelected = !!(liveData.hotSeat || liveData.host?.selectedHotSeat);
      if (phase !== SW_PHASES.USER_WHEEL_READY || hotSeatSelected) {
        setUwSpinning(false);
      }
    }
  }, [liveSpinning]);

  const clearLiveState = useCallback(() => {
    setIsLiveWheel(false);
    setLiveMode("participants");
    setHotSeatInfo(null);
    setPrizeWheelMeta(null);
    setLiveEntries([]);
    setLiveWinner(null);
    setLiveAnimComplete(false);
    setHostPreview(null);
    setHostPhase(null);
    setHostGameType(null);
    setAnnounceWheelData(null);
    setUserWheelEntries([]);
    setUwSpinning(false);
    setUwWinner(null);
    setUwAnimComplete(false);
  }, []);

  const fetchLiveWheel = useCallback(async ({ silent = false, reconcileMotion = false } = {}) => {
    const gen = ++liveFetchGenRef.current;
    try {
      if (!silent) setHostLoading(true);
      const liveRes = await fetch(`${API_BASE}/api/spinning-wheel/public/live-wheel`);
      if (gen !== liveFetchGenRef.current) return false;
      if (liveRes.ok) {
        const liveData = await liveRes.json();
        applyLivePayload(liveData, { reconcileMotion });
        return true;
      }
      clearLiveState();
      return false;
    } catch (err) {
      console.error("Error fetching live wheel:", err);
      if (gen !== liveFetchGenRef.current) return false;
      if (!silent) setError(err.message || "Failed to load live wheel");
      return false;
    } finally {
      if (gen === liveFetchGenRef.current && !silent) setHostLoading(false);
    }
  }, [applyLivePayload, clearLiveState]);

  useEffect(() => {
    fetchLiveWheelRef.current = fetchLiveWheel;
  }, [fetchLiveWheel]);

  useEffect(() => {
    applyLivePayloadRef.current = applyLivePayload;
  }, [applyLivePayload]);

  useEffect(() => {
    clearLiveStateRef.current = clearLiveState;
  }, [clearLiveState]);

  const fetchDefaultWheel = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setSoloLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE}/api/spinning-wheel/public/active-wheel`);
      if (!response.ok) {
        if (response.status === 404) {
          setWheel(null);
          setError("No default wheel is active yet. Check back in a moment.");
        } else {
          throw new Error("Failed to fetch wheel");
        }
        return;
      }
      const data = await response.json();
      setWheel(data.wheel);
    } catch (err) {
      console.error("Error fetching wheel:", err);
      setError(err.message || "Failed to load spinning wheel");
    } finally {
      if (!silent) setSoloLoading(false);
    }
  }, []);

  const redirectToMymForAuth = () => {
    if (window.top !== window) {
      window.top.location.href = `${window.location.origin}/mindyourmusic?openAuth=1&returnTo=spinning_wheel`;
    } else {
      navigate("/mindyourmusic", { state: { openAuth: true, returnTo: "spinning_wheel" } });
    }
  };

  const enterMode = (mode) => {
    setPlayMode(mode);
    goToGameScreen("game", { playMode: mode });
  };

  const handleSpin = () => {
    if (playMode !== "solo") return;
    if (!isAuthenticated || !user) {
      redirectToMymForAuth();
      return;
    }
    if (mustSpin || !wheel?.slices?.length) return;

    tryStartBgMusicRef.current();
    playRef.current("spin_start");

    const randomPrize = Math.floor(Math.random() * wheel.slices.length);
    setPrizeNumber(randomPrize);
    setMustSpin(true);
    setResult(null);
  };

  const handleStopSpinning = () => {
    setMustSpin(false);
    const landedSlice = wheel?.slices?.[prizeNumber];
    setResult(landedSlice);
    if (landedSlice) {
      firePrizeConfetti();
      playRef.current("winner");
      recordSpin(landedSlice);
    }
  };

  const isCurrentHotSeatPlayer = useCallback(() => {
    if (!hotSeatInfo) return false;
    const deviceId = getDeviceId();
    const myUserId = user?.id ?? user?.user_id;
    if (
      hotSeatInfo.user_id != null &&
      myUserId != null &&
      Number(hotSeatInfo.user_id) === Number(myUserId)
    ) {
      return true;
    }
    if (
      hotSeatInfo.device_id &&
      deviceId &&
      String(hotSeatInfo.device_id) === String(deviceId)
    ) {
      return true;
    }
    return false;
  }, [hotSeatInfo, user]);

  const handleHotSeatSpin = async () => {
    if (!isCurrentHotSeatPlayer()) {
      setError("Only the hot seat player can spin this wheel.");
      return;
    }
    if (liveSpinning || hotSeatSpinBusy) return;
    setHotSeatSpinBusy(true);
    setLiveWinner(null);
    setLiveAnimComplete(false);
    try {
      const res = await fetch(`${API_BASE}/api/spinning-wheel/hot-seat/spin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
          "x-device-id": getDeviceId(),
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Spin failed");
      setHotSeatInfo((prev) => (prev ? { ...prev, player_spin_unlocked: false } : prev));
      if (!socketRef.current?.connected) {
        setLivePrizeIndex(prizeIndexFromSpin(data.winnerIdx, data.winner?.id, liveEntries));
        setLiveFinalAngle(data.finalAngle);
        setLiveSpinDuration(data.spinDuration);
        setLiveSpinning(true);
        if (data.winner) setLiveWinner(data.winner);
      }
    } catch (err) {
      console.error("Hot seat spin error:", err);
      setError(err.message || "Failed to spin");
    } finally {
      setHotSeatSpinBusy(false);
    }
  };

  const recordSpin = async (landedSlice) => {
    try {
      const deviceId = getDeviceId();
      await fetch(`${API_BASE}/api/spinning-wheel/wheels/${wheel.wheel_id}/spins`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
          "x-device-id": deviceId,
        },
        body: JSON.stringify({
          slice_id: landedSlice.slice_id,
          points_awarded: landedSlice.points || 0,
          spin_result: landedSlice.text || `Slice ${landedSlice.slice_order}`,
        }),
      });
    } catch (err) {
      console.error("Error recording spin:", err);
    }
  };

  const handleLiveAnimEnd = useCallback(() => {
    setLiveSpinning(false);
    setLiveAnimComplete(true);
  }, []);

  useEffect(() => {
    if (isLoading) return;
    if (playMode === "pick") return;
    if (!isAuthenticated || !user) redirectToMymForAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated, user, navigate, playMode]);

  useEffect(() => {
    if (playMode === "solo") fetchDefaultWheel();
  }, [playMode, fetchDefaultWheel]);

  useEffect(() => {
    if (playMode === "host") fetchLiveWheel();
  }, [playMode, fetchLiveWheel]);

  useEffect(() => {
    if (playMode !== "pick") return undefined;
    fetchLiveWheel({ silent: true });
    const poll = setInterval(() => fetchLiveWheel({ silent: true }), 15000);
    return () => clearInterval(poll);
  }, [playMode, fetchLiveWheel]);

  useEffect(() => {
    if (playMode !== "host" || liveMode !== "hot_seat" || !hotSeatInfo) return;
    const validPhases = isWheelVisiblePhase(hostPhase)
      || hostPhase === SW_PHASES.SELECT_HOT_SEAT
      || hostPhase === SW_PHASES.HOT_SEAT_SLIDE
      || hostPhase === SW_PHASES.USER_WHEEL_READY;
    if (!validPhases) return;
    const key = `${hotSeatInfo.user_id || ""}:${hotSeatInfo.device_id || ""}:${hotSeatInfo.display_name || ""}`;
    if (!key || key === "::") return;
    if (announcedHotKeyRef.current === key) return;
    announcedHotKeyRef.current = key;
    setHotSeatAnnounce({
      display_name: hotSeatInfo.display_name,
      isSelf: isCurrentHotSeatPlayer(),
    });
  }, [playMode, liveMode, hotSeatInfo, isCurrentHotSeatPlayer, hostPhase]);

  // Phase-tied background music for Play with Host (Admin is source of truth for timing).
  useEffect(() => {
    if (playMode !== "host" || !soundsLoaded) return undefined;
    if (!isLiveWheel || !hostPhase) {
      stopBgMusic();
      return undefined;
    }
    // Enhanced Hot Seat flow phases
    if (hostPhase === SW_PHASES.INVITE) {
      if (sounds.invite_bg) playLoop("invite_bg");
      else if (sounds.intro_music) playLoop("intro_music");
      else playLoop("bg_music");
    } else if (hostPhase === SW_PHASES.ANNOUNCE) {
      if (sounds.announce_bg) playLoop("announce_bg");
      else if (sounds.game_about_to_start) playLoop("game_about_to_start");
      else playLoop("bg_music");
    } else if (hostPhase === SW_PHASES.HOT_SEAT_SLIDE) {
      if (sounds.hot_seat_slide_bg) playLoop("hot_seat_slide_bg");
      else if (sounds.game_about_to_start) playLoop("game_about_to_start");
      else playLoop("bg_music");
    } else if (hostPhase === SW_PHASES.LUCKY_PLAYER) {
      if (sounds.lucky_player_bg) playLoop("lucky_player_bg");
      else if (sounds.game_about_to_start) playLoop("game_about_to_start");
      else playLoop("bg_music");
    } else if (hostPhase === SW_PHASES.BUILDING_USER_WHEEL) {
      if (sounds.building_wheel) playLoop("building_wheel");
      else playLoop("bg_music");
    } else if (hostPhase === SW_PHASES.COUNTDOWN) {
      if (sounds.countdown_tick) play("countdown_tick");
      stopBgMusic();
    } else if (hostPhase === SW_PHASES.USER_WHEEL_READY) {
      stopBgMusic();
    // Legacy phases
    } else if (hostPhase === SW_PHASES.INTRO) {
      if (sounds.intro_music) playLoop("intro_music");
      else playLoop("bg_music");
    } else if (hostPhase === SW_PHASES.GAME_ABOUT_TO_START) {
      if (sounds.game_about_to_start) playLoop("game_about_to_start");
      else playLoop("bg_music");
    } else if (
      hostPhase === SW_PHASES.FORCE ||
      hostPhase === SW_PHASES.SELECT_HOT_SEAT
    ) {
      if (sounds.game_about_to_start) playLoop("game_about_to_start");
      else playLoop("bg_music");
    } else if (hostPhase === SW_PHASES.BUILDING_WHEEL) {
      if (sounds.building_wheel) playLoop("building_wheel");
      else if (sounds.game_about_to_start) playLoop("game_about_to_start");
      else playLoop("bg_music");
    } else if (isWheelVisiblePhase(hostPhase)) {
      stopBgMusic();
    } else {
      stopBgMusic();
    }
    return undefined;
  }, [
    playMode,
    isLiveWheel,
    hostPhase,
    soundsLoaded,
    sounds,
    play,
    playLoop,
    stopBgMusic,
  ]);

  useEffect(() => {
    const socket = io(API_BASE, { transports: ["websocket"] });
    socketRef.current = socket;

    const pullLiveSnapshot = () => {
      const mode = playModeRef.current;
      if (mode === "pick" || mode === "host") {
        fetchLiveWheelRef.current?.({ silent: true, reconcileMotion: true });
      }
    };

    socket.on("connect", () => {
      socket.emit("spinwheel:join-live");
      pullLiveSnapshot();
    });
    const unbindResync = bindGameResync(() => {
      if (socket.connected) socket.emit("spinwheel:join-live");
      pullLiveSnapshot();
    });

    const refreshIfNeeded = () => {
      const mode = playModeRef.current;
      if (mode === "pick" || mode === "host") {
        fetchLiveWheelRef.current?.({ silent: true });
      }
    };

    socket.on("spinwheel:host_updated", (payload) => {
      if (playModeRef.current === "pick" || playModeRef.current === "host") {
        if (payload?.success || payload?.host || payload?.phase) {
          applyLivePayloadRef.current?.(payload);
        } else {
          refreshIfNeeded();
        }
      }
    });

    socket.on("spinwheel:host_ended", () => {
      announcedHotKeyRef.current = null;
      clearLiveStateRef.current?.();
      if (playModeRef.current === "pick" || playModeRef.current === "host") {
        fetchLiveWheelRef.current?.({ silent: true });
      }
    });

    socket.on("spinwheel:host_force", refreshIfNeeded);

    socket.on("spinwheel:live_updated", (payload) => {
      if (payload?.host || payload?.ended) {
        refreshIfNeeded();
        return;
      }
      if (payload?.hotSeat) {
        refreshIfNeeded();
        return;
      }
      if (playModeRef.current === "pick") {
        fetchLiveWheelRef.current?.({ silent: true });
        return;
      }
      if (playModeRef.current === "host") {
        fetchLiveWheelRef.current?.({ silent: true });
      }
    });

    socket.on("spinwheel:hot_seat_updated", refreshIfNeeded);
    socket.on("spinwheel:hot_seat_selected", refreshIfNeeded);

    const applyLiveSpinStart = ({ finalAngle, spinDuration, winnerId, winnerIdx }) => {
      if (playModeRef.current !== "host") return;
      if (!isLiveRef.current) {
        fetchLiveWheelRef.current?.({ silent: true });
        return;
      }

      const currentPhase = hostPhaseRef.current;

      // User wheel spin (during USER_WHEEL_READY phase for hot seat selection)
      if (currentPhase === SW_PHASES.USER_WHEEL_READY) {
        playRef.current("spin_start");
        setUwWinner(null);
        setUwAnimComplete(false);
        setUwFinalAngle(finalAngle);
        setUwSpinDuration(spinDuration);
        setUwSpinning(true);
        setUserWheelEntries((prev) => {
          const w = prev.find((e) => e.id === winnerId);
          if (w) setTimeout(() => setUwWinner(w), 0);
          return prev;
        });
        return;
      }

      if (!isWheelVisiblePhase(currentPhase)) {
        fetchLiveWheelRef.current?.({ silent: true });
        return;
      }
      playRef.current("spin_start");
      setLiveWinner(null);
      setLiveAnimComplete(false);
      setLivePrizeIndex(prizeIndexFromSpin(winnerIdx, winnerId, liveEntriesRef.current));
      setLiveFinalAngle(finalAngle);
      setLiveSpinDuration(spinDuration);
      setLiveSpinning(true);
      const landed = liveEntriesRef.current.find((e) => e.id === winnerId);
      if (landed) setTimeout(() => setLiveWinner(landed), 0);
    };

    socket.on("spinwheel:spin_start", applyLiveSpinStart);
    socket.on("spinwheel:hot_seat_spin_start", applyLiveSpinStart);

    socket.on("spinwheel:spin_complete", ({ session, winner: w }) => {
      if (playModeRef.current !== "host") return;

      // User wheel spin complete (hot seat selection)
      if (hostPhaseRef.current === SW_PHASES.USER_WHEEL_READY) {
        if (w) setUwWinner(w);
        fetchLiveWheelRef.current?.({ silent: true });
        return;
      }

      if (liveModeRef.current !== "participants") return;
      if (session?.entries) {
        setLiveEntries(session.entries.filter((e) => !e.is_excluded));
      }
    });

    socket.on("spinwheel:hot_seat_spin_complete", ({ session }) => {
      if (playModeRef.current !== "host") return;
      if (liveModeRef.current !== "hot_seat") return;
      if (session?.entries) setLiveEntries(session.entries);
    });

    socket.on("spinwheel:ended", () => {
      setLiveSpinning(false);
      setLiveAnimComplete(false);
      setLiveWinner(null);
      setUwSpinning(false);
      setUwAnimComplete(false);
      setUwWinner(null);
      announcedHotKeyRef.current = null;
      if (playModeRef.current === "pick" || playModeRef.current === "host") {
        fetchLiveWheelRef.current?.({ silent: true });
      }
    });

    socket.on("spinwheel:hot_seat_ended", () => {
      setLiveSpinning(false);
      setLiveAnimComplete(false);
      setLiveWinner(null);
      setUwSpinning(false);
      setUwAnimComplete(false);
      setUwWinner(null);
      announcedHotKeyRef.current = null;
      if (playModeRef.current === "pick" || playModeRef.current === "host") {
        fetchLiveWheelRef.current?.({ silent: true });
      }
    });

    const poll = setInterval(() => {
      if (playModeRef.current === "host") fetchLiveWheelRef.current?.({ silent: true });
    }, 15000);

    return () => {
      unbindResync();
      clearInterval(poll);
      socket.emit("spinwheel:leave-live");
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hostLive = hostPreview?.liveMode || null;
  const hostAccent = hostLive === "hot_seat" ? SPIN.hot : hostLive === "participants" ? SPIN.live : SPIN.gold;
  const hostTitle =
    hostLive === "hot_seat"
      ? "Hot Seat"
      : hostLive === "participants"
        ? "Users Wheel"
        : "Play with Host";
  const hostChip =
    hostLive === "hot_seat" ? "HOT SEAT" : hostLive === "participants" ? "LIVE" : "HOST";
  const hostDesc =
    hostLive === "hot_seat"
      ? hostPreview?.phase && !isWheelVisiblePhase(hostPreview.phase)
        ? "Hot Seat is live with the host — join to follow the intro and wait for the wheel."
        : `The host put ${hostPreview?.hotName || "a player"} in the Hot Seat. Watch the prize wheel — or spin it if you're the one in the chair.`
      : hostLive === "participants"
        ? hostPreview?.phase && !isWheelVisiblePhase(hostPreview.phase)
          ? "Users Wheel is live with the host — join to follow along until the wheel appears."
          : "You're on the live Users Wheel. The host spins to pick a winner from the room. Win real prizes!"
        : "Join when the host starts Users Wheel or Hot Seat. The room goes live from here.";
  const hostCta = hostLive === "hot_seat" ? "Join Hot Seat →" : "Join the room →";
  const HostTileIcon = hostLive === "hot_seat" ? HotSeatIcon : LiveIcon;

  const renderPicker = () => (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 1,
        position: "relative",
        zIndex: 1,
      }}
    >
      <Typography
        sx={{
          fontFamily: SPIN.fontDisplay,
          fontWeight: 800,
          fontSize: { xs: "1.65rem", sm: "2.1rem", md: "2.4rem" },
          letterSpacing: "-0.02em",
          mb: 1,
          animation: `${spinFadeUp} 0.5s ease both`,
        }}
      >
        Pick your night
      </Typography>
      <Typography
        sx={{
          color: SPIN.muted,
          maxWidth: 420,
          mb: { xs: 3, sm: 4 },
          fontSize: { xs: "0.95rem", sm: "1.05rem" },
          lineHeight: 1.55,
          animation: `${spinFadeUp} 0.55s 0.06s ease both`,
        }}
      >
        One spin. One prize. Play the house wheel, or jump into whatever the host just went live with.
      </Typography>

      <Grid container spacing={2} justifyContent="center" sx={{ maxWidth: 720, mx: "auto", width: "100%" }}>
        <Grid item xs={12} sm={6}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => enterMode("solo")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                enterMode("solo");
              }
            }}
            sx={{ ...spinModeTileSx(SPIN.gold), animationDelay: "0.12s" }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
                background: "rgba(255,193,7,0.12)",
                color: SPIN.gold,
              }}
            >
              <SoloIcon />
            </Box>
            <Typography sx={{ fontFamily: SPIN.fontDisplay, fontWeight: 800, fontSize: "1.35rem", mb: 0.75 }}>
              Play Solo
            </Typography>
            <Typography sx={{ color: SPIN.muted, fontSize: "0.92rem", lineHeight: 1.5, mb: 2 }}>
              Spin the default wheel at your own pace. Just you, the slices, and whatever prize you land on.
            </Typography>
            <Typography
              sx={{
                fontFamily: SPIN.fontDisplay,
                fontWeight: 700,
                fontSize: "0.8rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: SPIN.gold,
              }}
            >
              Default wheel →
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => enterMode("host")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                enterMode("host");
              }
            }}
            sx={{ ...spinModeTileSx(hostAccent), animationDelay: "0.2s" }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
                background: `${hostAccent}1f`,
                color: hostAccent,
              }}
            >
              <HostTileIcon />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75, flexWrap: "wrap" }}>
              <Typography sx={{ fontFamily: SPIN.fontDisplay, fontWeight: 800, fontSize: "1.35rem" }}>
                {hostTitle}
              </Typography>
              <Chip
                size="small"
                icon={
                  hostLive === "hot_seat" ? (
                    <HotSeatIcon sx={{ fontSize: "14px !important" }} />
                  ) : (
                    <LiveIcon sx={{ fontSize: "14px !important" }} />
                  )
                }
                label={hostChip}
                sx={{
                  height: 22,
                  fontWeight: 800,
                  fontSize: "0.65rem",
                  letterSpacing: "0.06em",
                  bgcolor: `${hostAccent}33`,
                  color: hostAccent,
                  border: `1px solid ${hostAccent}55`,
                  "& .MuiChip-icon": { color: hostAccent },
                  animation: hostLive ? `${spinPulse} 2.2s ease-in-out infinite` : "none",
                }}
              />
            </Box>
            <Typography sx={{ color: SPIN.muted, fontSize: "0.92rem", lineHeight: 1.5, mb: 2 }}>
              {hostDesc}
            </Typography>
            <Typography
              sx={{
                fontFamily: SPIN.fontDisplay,
                fontWeight: 700,
                fontSize: "0.8rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: hostAccent,
              }}
            >
              {hostCta}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  const renderSoloWheel = () => {
    if (soloLoading) {
      return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, zIndex: 1 }}>
          <CircularProgress sx={{ color: SPIN.gold }} />
          <Typography sx={{ color: SPIN.muted }}>Loading the default wheel…</Typography>
        </Box>
      );
    }

    if (error && !wheel) {
      return (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: 2, zIndex: 1 }}>
          <Box sx={spinPanelSx}>
            <WheelIcon sx={{ fontSize: 40, color: SPIN.gold, mb: 1.5, opacity: 0.9 }} />
            <Typography sx={{ fontFamily: SPIN.fontDisplay, fontWeight: 700, mb: 1, fontSize: "1.15rem" }}>
              Wheel is warming up
            </Typography>
            <Typography sx={{ color: SPIN.muted, mb: 3, fontSize: "0.92rem", lineHeight: 1.5 }}>{error}</Typography>
            <Button variant="contained" onClick={() => fetchDefaultWheel()} startIcon={<RefreshIcon />} sx={spinPrimaryBtnSx}>
              Check again
            </Button>
          </Box>
        </Box>
      );
    }

    if (!wheel || !(wheel.slices || []).length) return null;

    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          overflow: "hidden",
          position: "relative",
          zIndex: 1,
          px: { xs: 1.5, sm: 2 },
          pt: { xs: 1, sm: 1.5 },
          animation: `${spinFadeUp} 0.45s ease both`,
        }}
      >
        <Typography
          sx={{
            fontFamily: SPIN.fontDisplay,
            fontWeight: 800,
            fontSize: { xs: "1.2rem", sm: "1.6rem" },
            letterSpacing: "-0.02em",
            textAlign: "center",
          }}
        >
          {wheel.wheel_name}
        </Typography>
        {wheel.description && (
          <Typography sx={{ color: SPIN.muted, fontSize: { xs: "0.82rem", sm: "0.95rem" }, mb: 0.5, textAlign: "center" }}>
            {wheel.description}
          </Typography>
        )}

        <Box
          ref={wheelBoxRef}
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            maxWidth: 560,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DefaultStyleWheel
            slices={wheel.slices}
            logoUrl={wheel.logo_url || "/mind_your_music.png"}
            mustStartSpinning={mustSpin}
            prizeNumber={prizeNumber}
            onStopSpinning={handleStopSpinning}
            spinning={mustSpin}
          />
        </Box>

        <Dialog
          open={!!result}
          onClose={() => setResult(null)}
          PaperProps={{
            sx: {
              borderRadius: "18px",
              overflow: "hidden",
              minWidth: 240,
              background: "linear-gradient(160deg, #2a2208 0%, #120e04 100%)",
              border: `1px solid ${SPIN.gold}55`,
            },
          }}
          BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,0.55)" } }}
        >
          <DialogContent sx={{ py: 3.5, px: 4, textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.72rem", letterSpacing: "0.14em", color: SPIN.gold, fontWeight: 700, mb: 1 }}>
              YOU LANDED ON
            </Typography>
            <Typography
              sx={{
                fontFamily: SPIN.fontDisplay,
                fontWeight: 800,
                fontSize: "1.35rem",
                color: SPIN.gold,
              }}
            >
              {result?.text || `Slice ${result?.slice_order}`}
            </Typography>
            {result?.points > 0 && (
              <Typography sx={{ color: SPIN.muted, mt: 0.75, fontWeight: 600 }}>
                +{result.points} points
              </Typography>
            )}
          </DialogContent>
        </Dialog>

        <Box
          sx={{
            flexShrink: 0,
            py: { xs: 1, sm: 1.5 },
            pb: { xs: "max(10px, env(safe-area-inset-bottom, 0px))", sm: 2 },
            display: "flex",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={mustSpin ? <CircularProgress size={18} color="inherit" /> : <PlayIcon />}
            onClick={handleSpin}
            disabled={mustSpin}
            sx={spinPrimaryBtnSx}
          >
            {mustSpin ? "Spinning..." : "Spin the Wheel"}
          </Button>
        </Box>
      </Box>
    );
  };

  const handleUwAnimEnd = useCallback(() => {
    setUwSpinning(false);
    setUwAnimComplete(true);
  }, []);

  const renderHostWheel = () => {
    const isHotSeat = liveMode === "hot_seat" || hostGameType === SW_GAME_TYPES.HOT_SEAT;
    const hotName = hotSeatInfo?.display_name || "";
    const centerLogo = "/mind_your_music.png";
    const playerCanSpin = !!hotSeatInfo?.player_can_spin;
    const playerSpinUnlocked = !!hotSeatInfo?.player_spin_unlocked;
    const isHotSeatPlayer = isHotSeat && isCurrentHotSeatPlayer();
    const showPlayerSpinControls =
      isHotSeat &&
      playerCanSpin &&
      isHotSeatPlayer &&
      liveEntries.length >= 2 &&
      isWheelVisiblePhase(hostPhase) &&
      !isUserWheelPhase(hostPhase);
    const showPlayerSpinButton = showPlayerSpinControls && playerSpinUnlocked;

    if (hostLoading && !isLiveWheel) {
      return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, zIndex: 1 }}>
          <CircularProgress sx={{ color: SPIN.gold }} />
          <Typography sx={{ color: SPIN.muted }}>Finding the live session…</Typography>
        </Box>
      );
    }

    if (!isLiveWheel) {
      return renderSpinHostPhaseView({
        phase: SW_PHASES.WAITING,
        gameType: null,
        hotSeatName: null,
        announceWheel: null,
      });
    }

    // Show user wheel during USER_WHEEL_READY phase
    if (isUserWheelPhase(hostPhase) && userWheelEntries.length >= 2) {
      return (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
            px: { xs: 1.5, sm: 2 },
            pt: { xs: 1, sm: 1.5 },
            animation: `${spinFadeUp} 0.45s ease both`,
          }}
        >
          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Typography
              sx={{
                fontFamily: SPIN.fontDisplay,
                fontWeight: 800,
                fontSize: { xs: "1.15rem", sm: "1.5rem" },
                letterSpacing: "-0.02em",
              }}
            >
              Who Gets the Hot Seat?
            </Typography>
            <Chip
              label="SELECTION WHEEL"
              size="small"
              icon={<LiveIcon sx={{ fontSize: "14px !important" }} />}
              sx={{
                mt: 0.75,
                height: 24,
                fontWeight: 800,
                fontSize: "0.68rem",
                letterSpacing: "0.06em",
                bgcolor: "rgba(255,45,138,0.2)",
                color: SPIN.live,
                border: `1px solid ${SPIN.live}55`,
                "& .MuiChip-icon": { color: SPIN.live },
                animation: `${spinPulse} 2.2s ease-in-out infinite`,
              }}
            />
            <Typography sx={{ color: SPIN.muted, mt: 0.75, fontSize: { xs: "0.82rem", sm: "0.92rem" }, maxWidth: 420, mx: "auto" }}>
              The host is spinning to select the hot seat player — watch the wheel!
            </Typography>
          </Box>

          <Box
            ref={wheelBoxRef}
            sx={{
              flex: 1,
              minHeight: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <LiveWheelDisplay
              entries={userWheelEntries}
              spinning={uwSpinning}
              finalAngle={uwFinalAngle}
              spinDuration={uwSpinDuration}
              winnerEntry={uwWinner}
              animationComplete={uwAnimComplete}
              onAnimationEnd={handleUwAnimEnd}
              size={wheelBoxSize}
              onWinnerReveal={() => playRef.current("winner")}
            />
          </Box>

          {uwWinner && uwAnimComplete && (
            <Box sx={{ py: 1, textAlign: "center" }}>
              <Typography
                sx={{
                  fontFamily: SPIN.fontDisplay,
                  fontWeight: 800,
                  fontSize: { xs: "1.1rem", sm: "1.3rem" },
                  color: SPIN.hot,
                }}
              >
                {uwWinner.display_name} is in the Hot Seat!
              </Typography>
              <Typography sx={{ color: SPIN.muted, fontSize: "0.85rem" }}>
                The prize wheel is coming up next…
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    const phaseView = renderSpinHostPhaseView({
      phase: hostPhase,
      gameType: hostGameType || (isHotSeat ? SW_GAME_TYPES.HOT_SEAT : SW_GAME_TYPES.USER_WHEEL),
      hotSeatName: hotName,
      announceWheel: announceWheelData,
    });
    if (phaseView) return phaseView;

    if (!isWheelVisiblePhase(hostPhase) || liveEntries.length < 2) {
      return renderSpinHostPhaseView({
        phase: SW_PHASES.BUILDING_WHEEL,
        gameType: hostGameType,
        hotSeatName: hotName,
        announceWheel: announceWheelData,
      });
    }

    let hotSeatSubtitle = "Host is controlling the wheel — watch the live spin!";
    if (isHotSeat) {
      if (playerCanSpin && isHotSeatPlayer && playerSpinUnlocked) {
        hotSeatSubtitle = "You're in the hot seat — you have one spin. Tap Spin!";
      } else if (playerCanSpin && isHotSeatPlayer) {
        hotSeatSubtitle = "Your spin is locked. Wait for the host to unlock it.";
      } else if (playerCanSpin && playerSpinUnlocked) {
        hotSeatSubtitle = `Watch — ${hotName || "the hot seat player"} can spin once from their device.`;
      } else if (playerCanSpin) {
        hotSeatSubtitle = `Waiting — the host will unlock one spin for ${hotName || "the hot seat player"}.`;
      } else {
        hotSeatSubtitle = "Watch only — the host is spinning this wheel for the hot seat player.";
      }
    } else {
      hotSeatSubtitle = "You're on the live Users Wheel. The host spins — hang tight for the winner.";
    }

    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          overflow: "hidden",
          position: "relative",
          zIndex: 1,
          px: { xs: 1.5, sm: 2 },
          pt: { xs: 1, sm: 1.5 },
          animation: `${spinFadeUp} 0.45s ease both`,
        }}
      >
        <HotSeatAnnounceModal
          open={!!hotSeatAnnounce}
          onClose={() => setHotSeatAnnounce(null)}
          displayName={hotSeatAnnounce?.display_name}
          isSelf={!!hotSeatAnnounce?.isSelf}
        />

        <Box sx={{ textAlign: "center", mb: 1 }}>
          <Typography
            sx={{
              fontFamily: SPIN.fontDisplay,
              fontWeight: 800,
              fontSize: { xs: "1.15rem", sm: "1.5rem" },
              letterSpacing: "-0.02em",
            }}
          >
            {liveTitle}
          </Typography>
          <Chip
            label={isHotSeat ? "HOT SEAT" : "USERS WHEEL"}
            size="small"
            icon={
              isHotSeat ? (
                <HotSeatIcon sx={{ fontSize: "14px !important" }} />
              ) : (
                <LiveIcon sx={{ fontSize: "14px !important" }} />
              )
            }
            sx={{
              mt: 0.75,
              height: 24,
              fontWeight: 800,
              fontSize: "0.68rem",
              letterSpacing: "0.06em",
              bgcolor: isHotSeat ? "rgba(255,107,61,0.2)" : "rgba(255,45,138,0.2)",
              color: isHotSeat ? SPIN.hot : SPIN.live,
              border: `1px solid ${isHotSeat ? SPIN.hot : SPIN.live}55`,
              "& .MuiChip-icon": { color: isHotSeat ? SPIN.hot : SPIN.live },
              animation: `${spinPulse} 2.2s ease-in-out infinite`,
            }}
          />
          {isHotSeat && hotName && (
            <Typography
              sx={{
                mt: 1,
                fontWeight: 800,
                fontFamily: SPIN.fontDisplay,
                color: SPIN.hot,
                fontSize: { xs: "1rem", sm: "1.2rem" },
              }}
            >
              Hot seat: {hotName}
            </Typography>
          )}
          <Typography sx={{ color: SPIN.muted, mt: 0.75, fontSize: { xs: "0.82rem", sm: "0.92rem" }, maxWidth: 420, mx: "auto" }}>
            {hotSeatSubtitle}
          </Typography>
        </Box>

        <Box
          ref={wheelBoxRef}
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <LiveWheelDisplay
            entries={liveEntries}
            spinning={liveSpinning}
            finalAngle={liveFinalAngle}
            spinDuration={liveSpinDuration}
            winnerEntry={liveWinner}
            animationComplete={liveAnimComplete}
            onAnimationEnd={handleLiveAnimEnd}
            size={isHotSeat ? Math.min(wheelBoxSize, 445) : wheelBoxSize}
            centerLogoSrc={centerLogo}
            hotSeatPlayerName={isHotSeat ? hotName : null}
            backgroundImageUrl={isHotSeat ? prizeWheelMeta?.background_image_url || "" : ""}
            onWinnerReveal={() => playRef.current("winner")}
            variant={isHotSeat ? "default" : "live"}
            prizeIndex={livePrizeIndex}
          />
        </Box>

        {showPlayerSpinControls && (
          <Box
            sx={{
              flexShrink: 0,
              py: { xs: 1, sm: 1.5 },
              pb: { xs: "max(10px, env(safe-area-inset-bottom, 0px))", sm: 2 },
              textAlign: "center",
            }}
          >
            {showPlayerSpinButton ? (
              <Button
                variant="contained"
                size="large"
                startIcon={
                  liveSpinning || hotSeatSpinBusy ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <PlayIcon />
                  )
                }
                onClick={handleHotSeatSpin}
                disabled={liveSpinning || hotSeatSpinBusy}
                sx={spinHotBtnSx}
              >
                {liveSpinning || hotSeatSpinBusy ? "Spinning..." : "Spin the Wheel"}
              </Button>
            ) : (
              <Chip
                icon={<HotSeatIcon />}
                label="Spin locked"
                sx={{
                  fontWeight: 800,
                  color: SPIN.muted,
                  border: `1px solid ${SPIN.muted}`,
                  bgcolor: "transparent",
                }}
                variant="outlined"
              />
            )}
          </Box>
        )}

        {error && (
          <Typography sx={{ color: SPIN.hot, fontSize: "0.85rem", mb: 1 }}>{error}</Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={spinPageSx}>
      {!isGigMode && (
        <AppBar position="static" sx={spinAppBarSx}>
          <Toolbar sx={{ position: "relative", minHeight: { xs: 48, sm: 56 }, px: { xs: 1, sm: 2 } }}>
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", zIndex: 1 }}>
              <Box
                component="img"
                src="/assets/spinthewheel.png"
                alt="Spin the Wheel"
                role={!forceJoin && playMode !== "pick" ? "button" : undefined}
                tabIndex={!forceJoin && playMode !== "pick" ? 0 : undefined}
                onClick={() => {
                  if (forceJoin || playMode === "pick") return;
                  goBackGameScreen();
                }}
                onKeyDown={(e) => {
                  if (forceJoin || playMode === "pick") return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    goBackGameScreen();
                  }
                }}
                sx={{
                  height: { xs: 32, sm: 44 },
                  objectFit: "contain",
                  cursor: !forceJoin && playMode !== "pick" ? "pointer" : "default",
                  filter: "drop-shadow(0 0 12px rgba(255,193,7,0.28))",
                }}
              />
            </Box>

            <Typography
              component="div"
              sx={{
                ...spinTitleGradientSx,
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                pointerEvents: "none",
                fontSize: { xs: "0.75rem", sm: "0.95rem" },
                display: { xs: "none", sm: "block" },
              }}
            >
              SPIN THE WHEEL
            </Typography>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 0.5,
                zIndex: 1,
              }}
            >
              <SoundMuteButton />
              <UserAuthButton />
            </Box>
          </Toolbar>
        </AppBar>
      )}
      {isGigMode && <SoundMuteButton floating />}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          zIndex: 1,
          overflow: "hidden",
          width: "100%",
        }}
      >
        {playMode === "pick" && renderPicker()}
        {playMode === "solo" && isAuthenticated && user && renderSoloWheel()}
        {playMode === "host" && isAuthenticated && user && renderHostWheel()}
      </Box>
    </Box>
  );
};

export default SpinningWheel;
